import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAppInstanceConfigs,
  selectCurrentAppInstanceConfigUuid,
  selectCurrentLocationId,
  selectHasVerticalAdapterNotNativeOrEssensys,
} from 'store/app-instance-configs/selectors';
import { updateCurrentAppInstanceConfigUuid, updateCurrentLocationId } from 'store/app-instance-configs/actions';
import { useLocation } from 'react-router-dom';
import { replace } from 'store/router/actions';
import { useIntl } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { selectIsSingleAppInstanceConfig } from 'store/config/selectors';

export const useCurrentLocation = (): string => {
  const dispatch = useDispatch();
  const location = useLocation();
  const intl = useIntl();
  const { resourceBookingPortfolioBased } = useFlags();

  const appInstanceConfigs = useSelector(selectAppInstanceConfigs);
  const locationId = useSelector(selectCurrentLocationId);
  const currentAppInstanceConfigUuid = useSelector(selectCurrentAppInstanceConfigUuid);
  const hasVerticalAdapterNotNativeOrEssensys = useSelector(selectHasVerticalAdapterNotNativeOrEssensys);
  const isSingleAppInstanceConfig = useSelector(selectIsSingleAppInstanceConfig);
  const currentLocationName = useMemo<string>(() => {
    if (locationId) {
      if (resourceBookingPortfolioBased && !isSingleAppInstanceConfig && !hasVerticalAdapterNotNativeOrEssensys) {
        const selectedLocationIds = locationId.split(',');
        // If all locations are selected, return "All locations"
        if (selectedLocationIds.length === appInstanceConfigs.length) {
          return intl.formatMessage({ id: 'all_locations' });
        }
      }
      return locationId
        ?.split(',')
        .map(locId => {
          const appInstanceConfig = appInstanceConfigs?.find(config => config.location_id === locId);

          return appInstanceConfig?.location_name;
        })
        .filter(locId => {
          return locId;
        })
        .join(', ');
    }
    const currentAppInstanceConfig = appInstanceConfigs?.find(
      config => config.uuid === currentAppInstanceConfigUuid && config.location_default,
    );

    return currentAppInstanceConfig?.location_name;
  }, [
    appInstanceConfigs,
    currentAppInstanceConfigUuid,
    hasVerticalAdapterNotNativeOrEssensys,
    intl,
    isSingleAppInstanceConfig,
    locationId,
    resourceBookingPortfolioBased,
  ]);

  const currentLocationId = useMemo<string>(() => {
    const appInstanceConfig = appInstanceConfigs?.find(config => config.location_name === currentLocationName);

    return appInstanceConfig?.location_id;
  }, [appInstanceConfigs, currentLocationName]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location?.search);
    queryParams.set('locationId', locationId);
    if (!location) {
      queryParams.delete('locationId');
    }
    const newUrl = `${location?.pathname}?${queryParams.toString()}`;

    dispatch(replace(newUrl));
    if (!locationId) {
      dispatch(updateCurrentLocationId({ currentLocationId }));
    }
  }, [dispatch, locationId, currentLocationId]);

  useEffect(() => {
    if (!locationId || !appInstanceConfigs?.length) {
      return;
    }

    const selectedLocationIds = locationId.split(',').filter(Boolean);
    const matchedConfigs = appInstanceConfigs.filter(config => selectedLocationIds.includes(config.location_id));
    const matchedConfigUuid = matchedConfigs[0]?.uuid;
    const isCurrentUuidMatchingSelection = matchedConfigs.some(config => config.uuid === currentAppInstanceConfigUuid);

    if (matchedConfigUuid && !isCurrentUuidMatchingSelection) {
      dispatch(updateCurrentAppInstanceConfigUuid({ currentAppInstanceConfigUuid: matchedConfigUuid }));
    }
  }, [appInstanceConfigs, currentAppInstanceConfigUuid, dispatch, locationId]);

  return currentLocationName;
};
